import qs from 'qs';
import type { FetchOptions } from 'ofetch';
import type { RouteLocationNormalized, RouteLocationRaw, RouteRecordRaw } from 'vue-router';

import type {
    PasswordCredentialsTokenRequest,
    AuthorizationCodeRequest,
    AuthorizationCodeTokenRequest,
} from '../types';
import type {
    RegisterFormDTO,
    ConfirmEmailFormDTO,
    ForgotPasswordFormDTO,
    RegisterResponse,
    ResetPasswordFormDTO,
} from '../types/dtos';

export const useAuth = () => {
    const userSession = useUserSessionStore();
    const router = useRouter();
    const requestURL = useRequestURL();

    const authBaseURL = useRuntimeConfig().public.authBaseURL;
    const options = useRuntimeConfig().public.auth;

    async function signIn(username: string, password: string, rememberMe: boolean, redirect?: RouteLocationRaw) {
        const data: PasswordCredentialsTokenRequest = {
            client_id: options.clientId,
            grant_type: 'password',
            username: username,
            password: password,
            scope: options.scope,
        };

        await _signIn(data, rememberMe, redirect);
    }

    async function signInAsUser(username: string, redirect?: RouteLocationRaw) {
        const data = {
            client_id: options.clientId,
            grant_type: 'login_as_user',
            username: username,
            return_to: router.currentRoute.value.path,
            scope: options.scope,
        };

        await _signIn(data, false, redirect ?? '/');
    }

    async function signInBackAsAdmin() {
        const data = {
            client_id: options.clientId,
            grant_type: 'login_back_as_admin',
            scope: options.scope,
        };

        await _signIn(data, false, userSession.userInfo?.return_to);
    }

    async function signInProvider(provider: string) {
        const codeVerifier = generateRandomString(64);
        const codeChallenge = await generateCodeChallenge(codeVerifier);

        sessionStorage.setItem('code_verifier', codeVerifier);

        const params: AuthorizationCodeRequest = {
            client_id: options.clientId,
            response_type: 'code',
            code_challenge: codeChallenge,
            code_challenge_method: 'S256',
            provider,
            scope: options.scope,
            redirect_uri: requestURL.origin + requestURL.pathname,
        };

        // Using navigateTo as some providers don't support CORS, so fetch won't work
        // An example of such provider is GitHub - https://github.com/isaacs/github/issues/330
        await navigateTo(authBaseURL + options.authorizeURL + '?' + new URLSearchParams(params).toString(), {
            external: true,
        });

        // const response = await useNuxtApp().$apiFetch.raw(options.authorizeURL, { params, baseURL: '', method: 'GET' });

        // if (response.redirected && response.url) {
        //     const redirectURL = new URL(response.url);
        //     await navigateTo(redirectURL.pathname + redirectURL.search);
        // }
    }

    async function _signIn(data: any, rememberMe: boolean, redirect?: RouteLocationRaw) {
        const response = await fetch(options.tokenURL, {
            body: qs.stringify(data),
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            method: 'POST',
        });

        userSession.setTokens(response.access_token, response.refresh_token, rememberMe);

        await updateUserInfo();

        if (redirect) await navigateTo(redirect);
    }

    async function requestToken(authorizationCode: string, redirect?: string) {
        const data: AuthorizationCodeTokenRequest = {
            client_id: options.clientId,
            grant_type: 'authorization_code',
            code: authorizationCode,
            code_verifier: sessionStorage.getItem('code_verifier') ?? undefined,
            redirect_uri: requestURL.origin + requestURL.pathname,
        };

        const response = await fetch(options.tokenURL, {
            body: qs.stringify(data),
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            method: 'POST',
        });

        userSession.setTokens(response.access_token, response.refresh_token);

        await updateUserInfo();

        if (redirect) await navigateTo(redirect);
    }

    async function updateUserInfo() {
        userSession.setUserInfo(await fetch(options.userInfoURL, { method: 'GET' }));
    }

    async function register(data: RegisterFormDTO) {
        return await fetch<RegisterResponse>(options.registerURL, { body: data, method: 'POST' });
    }

    async function resetPassword(data: ResetPasswordFormDTO) {
        return await fetch(options.resetPasswordURL, { body: data, method: 'POST' });
    }

    async function forgotPassword(data: ForgotPasswordFormDTO) {
        return await fetch(options.forgotPasswordURL, { body: data, method: 'POST' });
    }

    async function confirmEmail(data: ConfirmEmailFormDTO) {
        return await fetch(options.confirmEmailURL, { body: data, method: 'POST' });
    }

    async function signOut(redirect?: RouteLocationRaw) {
        if (userSession.isAuthenticated) 
            try {
                await fetch(options.logoutURL, { method: 'GET' });
            } catch {}

        userSession.clear();

        await navigateTo(redirect ?? options.logoutRedirectTo);
    }

    function isRouteAccessible(route: RouteRecordRaw | RouteLocationNormalized) {
        if (route.meta?.auth) {
            if (!userSession.isAuthenticated) return false;

            if (typeof route.meta.auth !== 'boolean') return userSession.isInRole(route.meta.auth.roles);
        }

        return true;
    }

    function generateRandomString(length: number) {
        const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let text = '';

        for (let i = 0; i < length; i++) text += possible.charAt(Math.floor(Math.random() * possible.length));

        return text;
    }

    async function generateCodeChallenge(codeVerifier: string) {
        const data = new TextEncoder().encode(codeVerifier);
        const digest = await crypto.subtle.digest('SHA-256', data);

        const base64Encoded = btoa(String.fromCharCode(...new Uint8Array(digest)));

        // To base64URL
        return base64Encoded.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    }

    async function fetch<T = any>(url: string, opts: FetchOptions & { method: 'GET' | 'POST' }) {
        return fetchApi<T>(url, { ...opts, baseURL: authBaseURL });
    }

    return {
        signIn,
        signInAsUser,
        signInBackAsAdmin,
        signInProvider,
        requestToken,
        register,
        resetPassword,
        forgotPassword,
        confirmEmail,
        signOut,
        updateUserInfo,
        isRouteAccessible,
    };
};
